import { MapViewportEventView } from '@mark43/cad-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { areLatLngsEqual } from '../../location/helpers';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { applyEventIconTheme, createEventFeatureLayer } from '../helpers/feature-layers';
import { createEventGraphic } from '../helpers/graphics';
import { getEventLocation } from '../helpers/locations';
import { EsriMapEntityTypeEnum } from '../types';
import type { MapMode } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { adjustMapviewportEventsLocations, getEventIconType } from '../helpers/events';
import { getMapIconColorMode } from '../helpers/map-icon-color-mode';
import { useMapIconTheme } from './use-map-icon-theme';
import { useSelectedEventFeatureLayer } from './use-selected-event-feature-layer';

type EventFeatureLayerProps = {
    mapViewportEvents: MapViewportEventView[];
    zoom: number;
    selectedEventId?: number;
    webMap?: WebMap;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    mapMode: MapMode;
};
export const useEventFeatureLayer = ({
    mapViewportEvents,
    zoom,
    selectedEventId,
    webMap,
    selectedMapLedgerEntityType,
    mapMode,
}: EventFeatureLayerProps) => {
    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.EVENTS>(
        EntityIdToObjectIdMaps.EVENTS
    );
    const [eventsLayer, setEventsLayer] = useState<FeatureLayer>();
    const mapIconTheme = useMapIconTheme(getMapIconColorMode(mapMode));
    const layerMapIconThemeRef = useRef(mapIconTheme);
    const prevMapIconTheme = usePreviousLayer(mapIconTheme, eventsLayer);
    const prevSelectedEventId = usePreviousLayer(selectedEventId, eventsLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        eventsLayer
    );

    const allEvents = adjustMapviewportEventsLocations(zoom, mapViewportEvents, selectedEventId);

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const eventFeatureLayer = createEventFeatureLayer(layerMapIconThemeRef.current);
            setEventsLayer(eventFeatureLayer);
            webMap.layers.add(eventFeatureLayer);
        }
    }, [webMap]);

    useEffect(
        function matchEventIconsToMapIconTheme() {
            if (!eventsLayer || layerMapIconThemeRef.current === mapIconTheme) {
                return;
            }
            applyEventIconTheme(eventsLayer, mapIconTheme);
            layerMapIconThemeRef.current = mapIconTheme;
        },
        [eventsLayer, mapIconTheme]
    );

    // event needs to be updated:
    // if selectedMapLedgerEntityType changed from EVENT or to EVENT
    // if event priority changed
    // if event lat/lng changed
    // if selectedEventId differs from prevSelectedEventId
    // if the map icon theme changed, since icon names depend on it
    const didEventGraphicChange = useCallback(
        (currEvent: MapViewportEventView, prevEvent: MapViewportEventView) => {
            const prevEventLocation = getEventLocation(prevEvent);
            const currEventLocation = getEventLocation(currEvent);

            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.EVENT ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.EVENT);

            const isPriorityChanged =
                prevEvent.priority !== currEvent.priority ||
                prevEvent.priorityAttributeId !== currEvent.priorityAttributeId;
            const isEventStatusChanged =
                getEventIconType(prevEvent.summaryCategory) !==
                getEventIconType(currEvent.summaryCategory);

            return (
                mapIconTheme !== prevMapIconTheme ||
                isEntityTypeChanged ||
                isPriorityChanged ||
                isEventStatusChanged ||
                !areLatLngsEqual(prevEventLocation, currEventLocation) ||
                (selectedEventId !== prevSelectedEventId &&
                    (currEvent.entityId === selectedEventId ||
                        currEvent.entityId === prevSelectedEventId))
            );
        },
        [
            mapIconTheme,
            prevMapIconTheme,
            prevSelectedEventId,
            selectedEventId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createEventGraphics = useCallback(
        (mapViewportEvents: MapViewportEventView[]) => {
            const eventGraphics: Graphic[] = [];
            for (const mapViewportEvent of mapViewportEvents) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    mapViewportEvent.entityId
                );
                const eventGraphic = createEventGraphic({
                    objectId,
                    mapViewportEvent,
                    selectedEventId,
                    selectedMapLedgerEntityType,
                });
                if (eventGraphic) {
                    eventGraphics.push(eventGraphic);
                }
            }
            return eventGraphics;
        },
        [selectedEventId, selectedMapLedgerEntityType]
    );

    useLayerControl<MapViewportEventView>({
        layer: eventsLayer,
        currItems: allEvents,
        didItemChange: didEventGraphicChange,
        createGraphics: createEventGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    useSelectedEventFeatureLayer({
        eventsLayer,
        webMap,
        events: allEvents,
        selectedEventId,
        selectedMapLedgerEntityType,
        mapIconTheme,
        didEventChange: didEventGraphicChange,
    });

    return { eventsLayer };
};
